/**
 * The mesh: a WireGuard overlay per tenant, coordinated by the platform.
 *
 * Every node (a workbench's agent, a phone, a web gateway) generates its own
 * WireGuard key pair and registers the public half. The platform allocates
 * an overlay address from the tenant's range, decides which peers each node
 * may see (owner and tags), and hands each node the peer list it needs to
 * build its WireGuard configuration. Private keys never leave the node.
 *
 *   POST /mesh/v1/nodes                 register or update (idempotent by publicKey)  -> NodeConfig
 *   GET  /mesh/v1/map?node=<id>         the node's current configuration (ETag)       -> NodeConfig
 *   POST /mesh/v1/nodes/:id/status      endpoints seen via STUN, peer handshakes
 *   POST /mesh/v1/nodes/:id/rotate      a new public key for the same node (same id and address)
 *   POST /mesh/v1/nodes/:id/flows       flow records, at most 500 a batch (round 3); duplicates are ignored
 *   DELETE /mesh/v1/nodes/:id           remove a node (owner or admin); its key is retired for good
 *   GET  /mesh/v1/nodes                 list (own nodes; every node for operator/admin)
 *   PATCH /admin/v1/mesh/nodes/:id      { tags?, name? } the platform-assigned tags; the node's name (and so its mesh name)
 *   GET/PUT /admin/v1/mesh/policy       who may reach whom (grants), who may assign which tag, key lifetime
 *   POST /admin/v1/mesh/policy/check    { policy } -> the tests' results and who gains or loses reach against the current policy
 *   GET  /admin/v1/mesh/flows           who talked to whom (administrators, operators, a node's owner for their node)
 *   GET  /admin/v1/mesh/relays          the relay instances: health, this organisation's sessions and throughput (administrators)
 *   GET  /admin/v1/mesh/gateway         whether the platform's gateway serves this organisation (administrators; mesh agent 1.5.0)
 *   GET/POST /admin/v1/mesh/webhooks    the organisation's webhooks; create one { url, events } -> { webhook, secret } (the secret is shown once)
 *   PATCH/DELETE /admin/v1/mesh/webhooks/:id   change the events or turn one on or off ({ events?, enabled? }); delete it
 *   POST /admin/v1/mesh/webhooks/:id/test       send a `test` event now and say how it went
 *   GET  /admin/v1/mesh/webhooks/:id/deliveries  the last 20 deliveries to one webhook
 *   GET  /mesh/v1/gateway/tenants       the gateway's organisations, paged (the gateway's credential only; mesh agent 1.5.0)
 *
 * Names: every node has a mesh name, `<name>.<tenant-slug>.mesh.internal`, unique in the tenant,
 * carried as `dnsName` on the node and on every peer, so forwards and tools name a node rather
 * than an address that may be reused after removal.
 *
 * Grants (policy version 2): `{ src, dst, ports }`, each side a list of selectors; a pair of nodes
 * peers when a grant matches either direction, and each node enforces per peer the ports that
 * peer may reach on it (`allowedPorts` on the peer stanza; empty means every port). Version 1
 * policies (`rules`, `ownerReachesOwnNodes`, `operatorsReachAll`) read as grants.
 *
 * Tags are the platform's, never the node's: a registration carries the tags the node asks for
 * (`tags`, kept as `requestedTags`), and the node's real tags are what an approver gave a headless
 * node, what an administrator set on a workbench, or nothing. Keys expire (`keyExpiresAt`, the
 * policy's `keyLifetimeDays`) and the agent rotates before then. Revoking the device, refusing the
 * node or removing a person disables their mesh nodes, which leave every peer map at the next poll.
 */
import { z } from "zod";
import { devicePostureSchema } from "./fleet.js";
export const MESH_SCHEMA_VERSION = 1;
/** WireGuard public key: 32 bytes, base64 (44 chars, ends with =). */
export const wireguardKeySchema = z.string().regex(/^[A-Za-z0-9+/]{43}=$/, "a base64 WireGuard key");
/** `node` is a headless workbench (`mojoup-node`); it serves the same control surface as a desktop. */
export const meshNodeKindSchema = z.enum(["workbench", "node", "mobile", "gateway", "relay"]);
/**
 * A tag name: `gpu`, `build-linux`. The `tag:` prefix the fleet and the workbench write (`tag:gpu`)
 * is accepted and dropped, so one spelling reaches the mesh whatever the sender uses.
 */
export const MESH_TAG_RE = /^(tag:)?[a-z0-9][a-z0-9-]{0,39}$/;
export const meshTagSchema = z
    .string()
    .regex(MESH_TAG_RE, "a tag: letters, digits and dashes, optionally with the tag: prefix")
    .transform((t) => (t.startsWith("tag:") ? t.slice(4) : t));
/** host:port the node can be reached at (public address from STUN, LAN addresses, a relay). */
export const meshEndpointSchema = z.string().regex(/^(\[[0-9a-fA-F:.]+\]|[0-9a-zA-Z.-]+):\d{1,5}$/, "host:port");
export const meshRegistrationSchema = z.object({
    schemaVersion: z.literal(MESH_SCHEMA_VERSION),
    publicKey: wireguardKeySchema,
    kind: meshNodeKindSchema,
    name: z.string().min(1).max(200),
    /** The workbench's device id when the node is a workbench agent. */
    deviceId: z.string().min(1).max(100).optional(),
    endpoints: z.array(meshEndpointSchema).max(16).default([]),
    /** The tags the node asks for; the platform decides (kept as `requestedTags` on the node). */
    tags: z.array(meshTagSchema).max(32).default([]),
    /** Ports the node serves on the overlay (informational, shown in the console). */
    services: z.array(z.object({ name: z.string().max(60), port: z.number().int().min(1).max(65535) })).max(32).default([]),
    agentVersion: z.string().max(40).optional(),
});
/** How a node's traffic travels: direct UDP, or through a relay over a WebSocket or an HTTPS stream. */
export const meshTransportSchema = z.enum(["udp", "relay-wss", "relay-https"]);
export const meshNodeSchema = z.object({
    id: z.string(),
    tenant: z.string(),
    ownerUserId: z.string(),
    ownerEmail: z.string().optional(),
    deviceId: z.string().optional(),
    kind: meshNodeKindSchema,
    name: z.string(),
    publicKey: wireguardKeySchema,
    /** Overlay address, e.g. 100.64.12.7 (a /32). */
    address: z.string(),
    /** The node's mesh name, `<name>.<tenant-slug>.mesh.internal`, unique in the tenant. */
    dnsName: z.string().optional(),
    endpoints: z.array(meshEndpointSchema),
    /** The platform-assigned tags the policy evaluates. */
    tags: z.array(z.string()),
    /** What the node asked for at registration; an administrator may accept them. */
    requestedTags: z.array(z.string()).default([]),
    services: z.array(z.object({ name: z.string(), port: z.number().int() })),
    agentVersion: z.string().optional(),
    /** How the node's traffic travels, as it last reported. */
    transport: meshTransportSchema.optional(),
    createdAt: z.string(),
    lastSeenAt: z.string(),
    /** When the current key stops being accepted; absent when the policy's lifetime is 0 (never). The agent rotates before it. */
    keyExpiresAt: z.string().optional(),
    online: z.boolean(),
    disabled: z.boolean(),
    /**
     * Why the node is off the mesh, when it is: an administrator, the device's revocation, the person's
     * removal, a refused node, an expired key, or (Mojo Up AI Cloud) the team's plan no longer carrying
     * the mesh (`plan-lapsed`; back on its own when the team is on Teams again).
     */
    disabledReason: z.enum(["admin", "device-revoked", "user-removed", "node-refused", "key-expired", "plan-lapsed"]).optional(),
    /** The posture its fleet device last reported (round 3); absent when the node has no device or the device reported none. */
    posture: devicePostureSchema.optional(),
    /**
     * An administrator turned key expiry off for this node (a server that must not drop off the mesh), and
     * the policy allows it (`keyExpiryOffAllowed`): `keyExpiresAt` is then absent. Mesh parity round.
     */
    keyExpiryDisabled: z.boolean().optional(),
});
/** POST /mesh/v1/nodes/:id/rotate: the node's next public key; the id and address stay. */
export const meshRotateKeySchema = z.object({ publicKey: wireguardKeySchema });
/**
 * PATCH /admin/v1/mesh/nodes/:id: the tags the platform assigns (replaces the set), the node's name, and
 * (administrators, mesh parity round) `keyExpiry`: `never` turns the node's key expiry off when the policy
 * allows it, `default` puts it back on the policy's `keyLifetimeDays`.
 */
export const meshNodeTagsPatchSchema = z.object({ tags: z.array(meshTagSchema).max(32).optional(), name: z.string().min(1).max(200).optional(), keyExpiry: z.enum(["default", "never"]).optional() });
/** A port spec: `22`, `8080-8090` or `*`. */
export const MESH_PORT_SPEC_RE = /^(\*|\d{1,5}(-\d{1,5})?)$/;
export const meshPortSpecSchema = z.string().regex(MESH_PORT_SPEC_RE, "a port, a range like 8080-8090, or *");
/**
 * A selector in a grant. Sources: `user:<id>`, `group:<group id>`, `tag:<tag>`, `kind:<kind>`,
 * `node:<node id>`, `autogroup:owner` (a person's own nodes, as source: each node reaches its
 * owner's other nodes), `autogroup:operators`, `autogroup:admins`, `autogroup:members` (every
 * person's nodes). Destinations: `tag:`, `kind:`, `node:`, `autogroup:self` (the source owner's own
 * nodes), `*`.
 */
export const MESH_SELECTOR_RE = /^(\*|(user|group|tag|kind|node):[^\s]{1,200}|autogroup:(owner|self|operators|admins|members))$/;
export const meshSelectorSchema = z.string().regex(MESH_SELECTOR_RE, "a selector: user:, group:, tag:, kind:, node:, autogroup:, or *");
/**
 * Device posture a grant may require of the source (round 3): what the machine reported on
 * registration (`posture` in fleet.ts). A node whose posture fails a grant's `srcPosture` is left
 * out of that grant, and the console says why.
 */
export const meshPostureRequirementSchema = z.object({
    diskEncrypted: z.boolean().optional(),
    firewall: z.boolean().optional(),
    screenLock: z.boolean().optional(),
    /** Minimum OS version per platform, e.g. { windows: "10.0.22631", darwin: "14.0", linux: "" }. */
    minOsVersion: z.record(z.string(), z.string().max(40)).optional(),
    /** Patched within this many days. */
    patchedWithinDays: z.number().int().min(1).max(365).optional(),
});
/** One grant: nodes matching any `src` reach nodes matching any `dst` on `ports` (empty: every port). */
export const meshGrantSchema = z.object({
    name: z.string().max(100).optional(),
    src: z.array(meshSelectorSchema).min(1).max(32),
    dst: z.array(meshSelectorSchema).min(1).max(32),
    ports: z.array(meshPortSpecSchema).max(64).default([]),
    /** Posture the source machine must report for the grant to apply. */
    srcPosture: meshPostureRequirementSchema.optional(),
});
/** A test the policy must pass before it is published: may `src` reach `dst` (on `port`)? */
export const meshPolicyTestSchema = z.object({
    name: z.string().max(100).optional(),
    src: meshSelectorSchema,
    dst: meshSelectorSchema,
    port: z.number().int().min(1).max(65535).optional(),
    allow: z.boolean(),
});
/** One WireGuard peer stanza. */
export const meshPeerSchema = z.object({
    nodeId: z.string(),
    name: z.string(),
    kind: meshNodeKindSchema,
    publicKey: wireguardKeySchema,
    address: z.string(),
    endpoints: z.array(meshEndpointSchema),
    /** Always the peer's /32 in v1. */
    allowedIps: z.array(z.string()),
    persistentKeepaliveSeconds: z.number().int().min(0).max(300),
    online: z.boolean(),
    tags: z.array(z.string()),
    /** The peer's mesh name, `<name>.<tenant-slug>.mesh.internal`. */
    dnsName: z.string().optional(),
    /** The fleet device behind the peer (a workbench or a headless node), so a desktop can match a peer to a machine it knows. */
    deviceId: z.string().optional(),
    /**
     * Ports this peer may reach on this node (port specs); absent or empty means every port. This node's agent enforces it on what it exposes.
     * `["0"]` (`MESH_NO_PORT`) means none: the pair peers only because this node reaches the peer.
     */
    allowedPorts: z.array(z.string()).optional(),
    /** Ports this node may reach on the peer, as the policy says; informational for forwards. Absent or empty means every port. */
    reachablePorts: z.array(z.string()).optional(),
    /**
     * The relay instance this pair meets on (`instance` of the map's relays; absent is 0). The platform
     * gives both ends the same one: a hash of the organisation and the two node ids (`relayShard`), or
     * 0 when either end's agent is older than relay sharding. Mesh agent 1.4.0.
     */
    relayInstance: z.number().int().min(0).optional(),
});
/**
 * A route to a relay the node may fall back to when no direct path exists (DERP-style; ciphertext only).
 * A deployment may run several relay instances: entries with the same `instance` reach the same relay
 * process (the platform's host passing through, then the relay's own host), in the order to try them,
 * and a pair of nodes meets on the instance its peer stanzas name (`relayInstance`). Absent is 0. An
 * agent older than sharding uses the first entry; the platform hands such an agent instance 0's
 * entries only and puts every pair it is in on instance 0.
 */
export const meshRelaySchema = z.object({
    id: z.string(),
    region: z.string(),
    host: z.string(),
    port: z.number().int(),
    publicKey: wireguardKeySchema.optional(),
    instance: z.number().int().min(0).optional(),
});
/**
 * What the platform's relays allow one organisation (0.29.0): open relay sessions across an instance,
 * and bytes carried a minute. `null` is no limit. A relay refuses a new session past `sessions` (429
 * with the reason in words) and drops packets past `bytesPerMinute`, as a congested path would. The
 * platform sets them from AIOE_RELAY_LIMITS or the organisation's plan (Mojo Up AI Cloud's Teams:
 * 100 sessions and 50 MB a minute; Enterprise: none) and names them in the map and in the relay's
 * verification answer; absent is no limit (and every platform before 0.29.0).
 */
export const meshRelayLimitsSchema = z.object({
    sessions: z.number().int().min(0).nullable(),
    bytesPerMinute: z.number().int().min(0).nullable(),
});
export const meshStunServerSchema = z.string().regex(/^[0-9a-zA-Z.-]+:\d{1,5}$/);
/**
 * How the organisation's mesh traffic travels (the policy's `transport.mode`):
 * - `relay-https` (the default): every node carries its traffic through the platform's relay over the
 *   HTTPS stream only: no direct UDP, no WebSocket relay;
 * - `relay`: through the relay over a WebSocket or the HTTPS stream, never direct UDP;
 * - `auto`: direct when possible, the relay as a fallback.
 */
export const meshTransportModeSchema = z.enum(["auto", "relay-https", "relay"]);
export const MESH_TRANSPORT_DEFAULT = "relay-https";
/**
 * The map's word on transport: `forced` is the relay transport every node must use, absent when the
 * policy is `auto` or the deployment lists no relay (forcing traffic through a relay that does not
 * exist would cut the mesh). When forced, the map carries no STUN servers and no peer endpoints, so
 * even an agent older than this field has no direct path to try.
 */
export const meshMapTransportSchema = z.object({ forced: z.enum(["relay-https", "relay"]).optional() });
/**
 * The map's posture block: the node's device posture, each grant whose `srcPosture` it fails (the
 * requirement, in words what it needs, and what the machine reported), and the nodes it would peer
 * with but for posture, each with a sentence a workbench can show as it is.
 */
export const meshMapPostureSchema = z.object({
    device: devicePostureSchema.optional(),
    failing: z.array(z.object({
        grant: z.string(),
        /** The `srcPosture` field: diskEncrypted, firewall, screenLock, minOsVersion, patchedWithinDays. */
        requirement: z.string(),
        /** What the grant needs, in words: "disk encryption", "Windows 10.0.22631 or later". */
        needs: z.string().optional(),
        /** What the machine reported, in words: "disk not encrypted". */
        actual: z.string(),
    })),
    missingPeers: z.array(z.object({
        nodeId: z.string(),
        name: z.string(),
        dnsName: z.string().optional(),
        /** "Your organisation's mesh needs disk encryption on this computer before it can reach build-01." */
        because: z.string(),
    })),
});
/** Everything a node needs to build its WireGuard interface. */
export const meshNodeConfigSchema = z.object({
    schemaVersion: z.literal(MESH_SCHEMA_VERSION),
    /** Changes whenever anything below changes; send back as If-None-Match. */
    revision: z.string(),
    node: meshNodeSchema,
    /** The tenant's overlay range, e.g. 100.64.0.0/16. */
    tenantCidr: z.string(),
    /** Overlay MTU the agent should use. */
    mtu: z.number().int().min(1280).max(1500),
    peers: z.array(meshPeerSchema),
    relays: z.array(meshRelaySchema),
    stun: z.array(meshStunServerSchema),
    pollIntervalSeconds: z.number().int().min(5).max(3600),
    keepaliveSeconds: z.number().int().min(0).max(300),
    /** How traffic must travel (0.23.0); absent from older platforms, which never force the relay. */
    transport: meshMapTransportSchema.optional(),
    /** What the node's posture keeps it from (0.23.0), in words; absent from older platforms. */
    posture: meshMapPostureSchema.optional(),
    /** What the relays allow the node's organisation (0.29.0); absent when nothing is limited. */
    relayLimits: meshRelayLimitsSchema.optional(),
});
export const meshStatusSchema = z.object({
    endpoints: z.array(meshEndpointSchema).max(16).optional(),
    handshakes: z.array(z.object({ peerPublicKey: wireguardKeySchema, at: z.string() })).max(1000).optional(),
    agentVersion: z.string().max(40).optional(),
    transport: meshTransportSchema.optional(),
    /** Observability (round 3): per peer, how the traffic travels and how much, as the agent counts it. */
    peers: z
        .array(z.object({
        peerPublicKey: wireguardKeySchema,
        via: z.enum(["direct", "relay"]).optional(),
        rxBytes: z.number().int().nonnegative().optional(),
        txBytes: z.number().int().nonnegative().optional(),
        lastHandshake: z.string().optional(),
        refused: z.number().int().nonnegative().optional(),
        /** The relay route (`meshRelaySchema.id`) carrying this pair when relayed: with several relay instances each pair has its own (mesh agent 1.4.0). */
        relayId: z.string().max(100).optional(),
    }))
        .max(1000)
        .optional(),
    /** The relay in use, when relayed. */
    relayId: z.string().max(100).optional(),
});
/**
 * Flow logs (round 3): one record per connection an agent accepted or refused on an exposed port,
 * or opened through a forward, batched to `POST /mesh/v1/nodes/:id/flows { flows }` (at most 500
 * a batch; the agent sends every minute or when 500 gather). The platform keeps them for the
 * tenant's usage retention, answers `GET /admin/v1/mesh/flows?nodeId=&peerNodeId=&since=&until=`
 * ("who talked to this workbench"), and exports refused ones to Sentinel with the audit stream.
 */
export const meshFlowSchema = z.object({
    peerNodeId: z.string().max(100),
    direction: z.enum(["in", "out"]),
    port: z.number().int().min(1).max(65535),
    startedAt: z.string(),
    endedAt: z.string().optional(),
    bytesIn: z.number().int().nonnegative().default(0),
    bytesOut: z.number().int().nonnegative().default(0),
    via: z.enum(["direct", "relay"]).optional(),
    /** True when the port policy refused the connection at accept. */
    refused: z.boolean().default(false),
});
export const meshFlowBatchSchema = z.object({ flows: z.array(meshFlowSchema).min(1).max(500) });
/** The JWS type the platform signs a relay's `GET /stats` request with (the relay checks it against `/.well-known/aioe-jwks.json`). */
export const RELAY_STATS_TYP = "aioe-relay-stats+jws";
/** A relay instance's numbers for one organisation, or for the instance in all. Throughput is the last whole minute's. */
export const meshRelayCountsSchema = z.object({
    sessions: z.number().int().nonnegative(),
    bytesPerMinute: z.number().int().nonnegative(),
    /** Packets whose destination was not connected to this instance: a peer offline, or two ends sent to different instances. */
    missesPerMinute: z.number().int().nonnegative(),
    /** Bytes dropped in the last whole minute because the organisation was past its `bytesPerMinute` (mesh services 1.5.0). */
    throttledPerMinute: z.number().int().nonnegative().optional(),
    /** Sessions refused in the last whole minute because the organisation held its `sessions` (mesh services 1.5.0). */
    refusedPerMinute: z.number().int().nonnegative().optional(),
});
/** What a relay instance answers the platform on `GET /stats` (mesh services 1.4.0). */
export const meshRelayStatsSchema = meshRelayCountsSchema.extend({
    instance: z.number().int().min(0),
    instances: z.number().int().min(1),
    version: z.string().optional(),
    startedAt: z.string(),
    total: meshRelayCountsSchema,
});
/**
 * The agent's local status file (<state-dir>/status.json), which the
 * workbench desktop app reads. Mirrors `state.Status` in agent/internal/state.
 */
export const meshAgentStatusFileSchema = z.object({
    pid: z.number().int(),
    version: z.string(),
    state: z.string(),
    error: z.string().optional(),
    url: z.string(),
    nodeId: z.string().optional(),
    name: z.string(),
    publicKey: z.string(),
    address: z.string().optional(),
    /** This node's mesh name, `<name>.<tenant-slug>.mesh.internal`. */
    dnsName: z.string().optional(),
    tenantCidr: z.string().optional(),
    listenPort: z.number().int().optional(),
    endpoints: z.array(z.string()),
    revision: z.string().optional(),
    /** When the node's key stops being accepted; absent means never. The agent rotates before it. */
    keyExpiresAt: z.string().optional(),
    /** Why the platform took the node off the mesh (with state "retired" when its key was retired). */
    disabledReason: z.string().optional(),
    /**
     * With state "not-in-plan" (mesh agent 1.5.1, exit code 5): the platform's words for why the
     * organisation's plan leaves this machine off the mesh (403 mesh_not_in_plan, a registration's 402
     * plan_limit, or the node taken off as plan-lapsed). The agent has stopped; start it again when the plan changes.
     */
    reason: z.string().optional(),
    exposed: z.array(z.object({ name: z.string(), listen: z.string(), target: z.string() })),
    forwards: z.array(z.object({ name: z.string(), listen: z.string(), target: z.string() })),
    peers: z.array(z.object({
        nodeId: z.string(),
        name: z.string(),
        kind: z.string(),
        publicKey: z.string(),
        address: z.string(),
        endpoint: z.string().optional(),
        online: z.boolean(),
        lastHandshake: z.string().optional(),
        rxBytes: z.number(),
        txBytes: z.number(),
        via: z.enum(["direct", "relay"]).optional(),
        /** The peer's mesh name. */
        dnsName: z.string().optional(),
        /** Connections from this peer to exposed ports that its allowedPorts refused, since the agent started. */
        refused: z.number().int().optional(),
    })),
    transport: meshTransportSchema,
    /** The relay transport the organisation's policy forces (0.23.0), when it does: the node opens no UDP socket. */
    transportForced: z.enum(["relay-https", "relay"]).optional(),
    relay: z
        .object({
        url: z.string(),
        transport: z.enum(["wss", "https"]),
        state: z.enum(["connecting", "connected", "error"]),
        error: z.string().optional(),
        since: z.string(),
        /**
         * Mesh agent 1.5.1: the relay is dropping the organisation's packets past the bytes a minute its
         * plan allows there. Cleared a minute and fifteen seconds after the relay's last notice.
         */
        throttled: z.boolean().optional(),
        /** Bytes the relay dropped for the organisation in the last minute (only while throttled). */
        throttledPerMinute: z.number().int().nonnegative().optional(),
        /** The organisation's bytes a minute on the relay, as its notice said (only while throttled). */
        limitPerMinute: z.number().int().nonnegative().optional(),
        /** When the relay's latest throttle notice came; kept after `throttled` clears. */
        lastThrottledAt: z.string().optional(),
    })
        .optional(),
    proxy: z.object({ url: z.string(), auth: z.enum(["none", "basic", "required"]) }).optional(),
    /** Flow records (round 3) waiting to be sent, sent since the agent started, and dropped (the oldest beyond 5,000 held). Absent with flow logs off. */
    flowsPending: z.number().int().nonnegative().optional(),
    flowsSent: z.number().int().nonnegative().optional(),
    flowsDropped: z.number().int().nonnegative().optional(),
    updatedAt: z.string(),
});
/**
 * Who may reach whom. Evaluated per ordered pair (from, to); a pair is allowed
 * when any clause matches. Owners always reach their own nodes when
 * `ownerReachesOwnNodes` is on; operators and admins reach everything when
 * `operatorsReachAll` is on. Tag rules connect groups of nodes.
 */
export const meshPolicySchema = z.object({
    /** 1: `rules` with the two switches; 2: `grants`. A version 1 policy reads as grants (`grantsOf`). */
    version: z.union([z.literal(1), z.literal(2)]).default(2),
    ownerReachesOwnNodes: z.boolean().default(true),
    /** Evaluated on the owner's roles as they are now, not as they were at registration. */
    operatorsReachAll: z.boolean().default(true),
    /**
     * Who may assign each tag: `admin` (always may), `operator`, or `approver` (whoever approves a
     * headless node may give it the tag). A tag not listed is administrators only.
     */
    tagOwners: z.record(z.string().regex(MESH_TAG_RE), z.array(z.enum(["admin", "operator", "approver"]))).default({}),
    /** Days a node's key is accepted before the agent must rotate it; 0 never expires. */
    keyLifetimeDays: z.number().int().min(0).max(3650).default(180),
    /**
     * Whether an administrator may turn key expiry off for one node (`PATCH /admin/v1/mesh/nodes/:id
     * { keyExpiry: "never" }`); absent is allowed. `false` puts every such node back on `keyLifetimeDays`.
     */
    keyExpiryOffAllowed: z.boolean().optional(),
    /** Version 2: who reaches whom on which ports. */
    grants: z.array(meshGrantSchema).max(200).default([]),
    /**
     * How traffic travels (0.23.0, either version): `relay-https` by default, so a policy stored
     * without it forces the relay over HTTPS. See `meshTransportModeSchema`.
     */
    transport: z.object({ mode: meshTransportModeSchema.default(MESH_TRANSPORT_DEFAULT) }).default({ mode: MESH_TRANSPORT_DEFAULT }),
    /** Checked on every publish; a failing test refuses the policy (400 `policy_tests_failed`). */
    tests: z.array(meshPolicyTestSchema).max(200).default([]),
    /** Version 1 (still accepted): tag rules, read as grants `tag:from -> tag:to`. */
    rules: z
        .array(z.object({
        name: z.string().max(100).optional(),
        fromTags: z.array(z.string()).min(1),
        toTags: z.array(z.string()).min(1),
        /** Empty = every port. Informational for agents that filter; the overlay itself is per-peer. */
        ports: z.array(z.number().int().min(1).max(65535)).default([]),
    }))
        .default([]),
});
/**
 * The grants a policy means, version 1 or 2: the two switches and the tag rules become grants
 * so one evaluator serves both. `ownerReachesOwnNodes` is `autogroup:owner -> autogroup:self`;
 * `operatorsReachAll` is `autogroup:operators -> *`.
 */
export function grantsOf(p) {
    const out = [];
    if (p.ownerReachesOwnNodes)
        out.push({ name: "Owners reach their own nodes", src: ["autogroup:owner"], dst: ["autogroup:self"], ports: [] });
    if (p.operatorsReachAll)
        out.push({ name: "Operators reach everything", src: ["autogroup:operators"], dst: ["*"], ports: [] });
    for (const r of p.rules)
        out.push({ ...(r.name ? { name: r.name } : {}), src: r.fromTags.map((t) => `tag:${t}`), dst: r.toTags.map((t) => `tag:${t}`), ports: r.ports.map(String) });
    return [...out, ...p.grants];
}
/** The port spec that names no port: `allowedPorts: ["0"]` lets the peer reach nothing on this node. */
export const MESH_NO_PORT = "0";
/** True when `port` is within any of the specs; an empty list means every port. */
export function portAllowed(port, specs) {
    if (!specs || specs.length === 0)
        return true;
    return specs.some((s) => {
        if (s === "*")
            return true;
        const [a, b] = s.split("-").map(Number);
        return b === undefined ? port === a : port >= a && port <= b;
    });
}
/** The mesh name for a node: its name as a label, unique in the tenant by a numeric suffix the platform adds. */
export function meshNameLabel(name) {
    const label = name.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 63);
    return label || "node";
}
export const MESH_NAME_SUFFIX = "mesh.internal";
/** The tag without its optional `tag:` prefix. */
export function normaliseMeshTag(t) {
    return t.startsWith("tag:") ? t.slice(4) : t;
}
/**
 * The organisation's overlay range for its range index: the (index mod 64)-th /16 of 100.64.0.0/10,
 * so 0 is 100.64.0.0/16 and 63 is 100.127.0.0/16, as they always were. From index 64 on, organisations
 * share ranges (mesh agent 1.5.0): an address is unique within its organisation only, which is all
 * WireGuard needs, because a node is only ever handed its own organisation's peers, the relay forwards
 * between nodes of one organisation only, and the platform's gateway holds one device per organisation.
 */
export function tenantCidrFor(index) {
    if (!Number.isInteger(index) || index < 0)
        throw new Error("tenant mesh index out of range");
    return `100.${64 + (index % 64)}.0.0/16`;
}
/** The i-th host address inside a /16 (skipping .0.0 and .0.1, which are reserved). */
export function addressInCidr(cidr, i) {
    const [base] = cidr.split("/");
    const [a, b] = base.split(".").map(Number);
    const host = i + 2;
    if (host >= 65534)
        throw new Error("tenant mesh range exhausted");
    return `${a}.${b}.${Math.floor(host / 256)}.${host % 256}`;
}
/** Dotted OS version numbers compared part by part ("10.0.22631" > "10.0.19045"); a missing part is 0. */
export function compareOsVersions(a, b) {
    const pa = (a.match(/\d+/g) ?? []).map(Number);
    const pb = (b.match(/\d+/g) ?? []).map(Number);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const d = (pa[i] ?? 0) - (pb[i] ?? 0);
        if (d)
            return d < 0 ? -1 : 1;
    }
    return 0;
}
const OS_WORDS = { windows: "Windows", darwin: "macOS", linux: "Linux", ios: "iOS", android: "Android" };
/**
 * What a device's posture fails of a grant's `srcPosture`, in words; empty when it meets every
 * requirement. A field the device did not report fails its requirement. `minOsVersion` applies to
 * the platforms it names (an empty minimum, or a platform not named, has none). `patchedWithinDays`
 * reads `patchedAt`.
 */
export function postureFailures(posture, req, now) {
    const p = posture ?? {};
    const out = [];
    const flag = (k, on, off, unknown) => {
        if (req[k] === undefined)
            return;
        if (p[k] === undefined)
            out.push({ requirement: k, message: unknown });
        else if (p[k] !== req[k])
            out.push({ requirement: k, message: p[k] ? on : off });
    };
    flag("diskEncrypted", "disk encrypted", "disk not encrypted", "disk encryption not reported");
    flag("firewall", "firewall on", "firewall off", "firewall not reported");
    flag("screenLock", "screen lock on", "screen lock off", "screen lock not reported");
    if (req.minOsVersion && Object.values(req.minOsVersion).some(Boolean)) {
        if (!p.os)
            out.push({ requirement: "minOsVersion", message: "operating system not reported" });
        else {
            const min = req.minOsVersion[p.os];
            if (min) {
                if (!p.osVersion)
                    out.push({ requirement: "minOsVersion", message: `${OS_WORDS[p.os] ?? p.os} version not reported` });
                else if (compareOsVersions(p.osVersion, min) < 0)
                    out.push({ requirement: "minOsVersion", message: `${OS_WORDS[p.os] ?? p.os} ${p.osVersion} is older than ${min}` });
            }
        }
    }
    if (req.patchedWithinDays !== undefined) {
        const at = p.patchedAt ? Date.parse(p.patchedAt) : NaN;
        if (Number.isNaN(at))
            out.push({ requirement: "patchedWithinDays", message: "last update not reported" });
        else {
            const days = Math.floor((now.getTime() - at) / 86_400_000);
            if (days > req.patchedWithinDays)
                out.push({ requirement: "patchedWithinDays", message: `last updated ${days} days ago, more than ${req.patchedWithinDays}` });
        }
    }
    return out;
}
/**
 * What one requirement of a grant's `srcPosture` asks for, in words, for the machine's platform:
 * "disk encryption", "a firewall", "Windows 10.0.22631 or later", "updates installed in the last 30 days".
 */
export function postureNeedInWords(req, k, posture) {
    switch (k) {
        case "diskEncrypted":
            return req.diskEncrypted === false ? "disk encryption off" : "disk encryption";
        case "firewall":
            return req.firewall === false ? "the firewall off" : "a firewall";
        case "screenLock":
            return req.screenLock === false ? "no screen lock" : "a screen lock";
        case "minOsVersion": {
            const os = posture?.os;
            const min = os ? req.minOsVersion?.[os] : undefined;
            return os && min ? `${OS_WORDS[os] ?? os} ${min} or later` : "its operating system and version reported";
        }
        case "patchedWithinDays":
            return `updates installed in the last ${req.patchedWithinDays} ${req.patchedWithinDays === 1 ? "day" : "days"}`;
    }
}
/** "a", "a and b", "a, b and c". */
export function meshListInWords(items) {
    if (items.length <= 1)
        return items[0] ?? "";
    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}
/**
 * Gateway per tenant (mesh agent 1.5.0). One `aioe-gateway` holds a node and a device per organisation.
 * The API names the organisation of every call it hands the gateway in this header (the tenant id); a
 * gateway serving one organisation ignores it.
 */
export const MESH_GATEWAY_TENANT_HEADER = "x-aioe-tenant";
/** The slug in the gateway's deployment credential, `aioe_gw_platform.<hex HMAC-SHA256("platform") keyed by the gateway secret>`. */
export const MESH_GATEWAY_PLATFORM_SLUG = "platform";
/**
 * Mesh webhooks (docs/mesh-webhooks.md): an organisation's administrators give
 * an https address and the events to send; each event is one mesh audit row put in the common mesh-webhook
 * shape, posted as a JSON array signed with `X-Aioe-Signature` (the audit webhook's headers).
 */
export const MESH_WEBHOOK_VERSION = 1;
/** A webhook is turned off after this many deliveries failing in a row; an administrator turns it back on. */
export const MESH_WEBHOOK_MAX_FAILURES = 10;
/** The deliveries a webhook's history keeps. */
export const MESH_WEBHOOK_DELIVERY_KEEP = 20;
export const MESH_WEBHOOK_GROUPS = ["Machines", "Keys", "Policy", "Webhooks"];
/** Every event a webhook may subscribe to, in the order the console lists them. `test` is only ever sent by the test route. */
export const MESH_WEBHOOK_EVENTS = [
    { type: "nodeCreated", group: "Machines", text: "A machine joins the mesh" },
    { type: "nodeApproved", group: "Machines", text: "A machine is approved" },
    { type: "nodeDeleted", group: "Machines", text: "A machine is taken off the mesh" },
    { type: "nodeDisabled", group: "Machines", text: "A machine is turned off" },
    { type: "nodeEnabled", group: "Machines", text: "A machine is turned back on" },
    { type: "nodeRenamed", group: "Machines", text: "A machine is renamed" },
    { type: "tagChanged", group: "Machines", text: "A machine's tags change" },
    { type: "nodeKeyExpiringInLessThan24Hours", group: "Keys", text: "A machine's key expires within a day" },
    { type: "nodeKeyExpired", group: "Keys", text: "A machine's key has expired" },
    { type: "nodeKeyRotated", group: "Keys", text: "A machine rotates its key" },
    { type: "nodeKeyRejected", group: "Keys", text: "A machine's key is refused" },
    { type: "nodeKeyExpiryChanged", group: "Keys", text: "Key expiry is turned off or on for a machine" },
    { type: "policyUpdated", group: "Policy", text: "The access policy is published" },
    { type: "webhookDisabled", group: "Webhooks", text: "A webhook is turned off after repeated failures" },
];
export const meshWebhookEventNameSchema = z.string().refine((t) => MESH_WEBHOOK_EVENTS.some((e) => e.type === t), "an event a webhook may subscribe to");
export const meshWebhookCreateSchema = z.object({
    url: z.string().url().max(2000),
    events: z.array(meshWebhookEventNameSchema).min(1).max(32),
});
export const meshWebhookPatchSchema = z.object({
    events: z.array(meshWebhookEventNameSchema).min(1).max(32).optional(),
    enabled: z.boolean().optional(),
});
//# sourceMappingURL=mesh.js.map